import { useContext } from "react";
import { AuthContext } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  User,
  Mail,
  ShieldCheck,
  LogOut,
  Info
} from "lucide-react";
import { toast } from "sonner";

const SettingsPage = () => {
  const { user, logout } = useContext(AuthContext);

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-2xl">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight" data-testid="settings-title">
            Settings
          </h1>
          <p className="text-slate-500 font-body mt-1">
            Manage your account and preferences
          </p>
        </div>

        {/* Profile Card */}
        <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="profile-card">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4">
            <CardTitle className="text-lg font-heading font-semibold text-slate-900">Profile</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-start gap-6">
              <Avatar className="w-20 h-20">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-slate-100 text-slate-600 font-heading font-semibold text-2xl">
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-4">
                <div>
                  <label className="text-sm text-slate-500 font-body flex items-center gap-1">
                    <User className="w-3 h-3" />
                    Name
                  </label>
                  <p className="font-body font-medium text-slate-900 mt-1">{user?.name}</p>
                </div>
                <div>
                  <label className="text-sm text-slate-500 font-body flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    Email
                  </label>
                  <p className="font-body text-slate-900 mt-1">{user?.email}</p>
                </div>
                <div>
                  <label className="text-sm text-slate-500 font-body flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" />
                    Role
                  </label>
                  <div className="mt-1">
                    {user?.role === "admin" ? (
                      <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
                        Admin
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-slate-600">
                        Team Member
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card className="bg-slate-50 border border-slate-200 rounded-sm" data-testid="account-info-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-slate-200 rounded-sm flex items-center justify-center flex-shrink-0">
                <Info className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="font-body font-medium text-slate-900">Account Information</p>
                <p className="text-sm text-slate-500 font-body mt-1">
                  Your account is managed through Google OAuth. To update your profile picture or name, 
                  please update your Google account settings and sign in again.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logout */}
        <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="logout-card">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4">
            <CardTitle className="text-lg font-heading font-semibold text-slate-900">Session</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-slate-600 font-body text-sm mb-4">
              Sign out of your account on this device.
            </p>
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700 font-body rounded-md"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default SettingsPage;
